import type { NavigationGuard } from 'vue-router'
import { useAccessControlService } from '@A/services/access-control.service'
import { useKeycloak } from '@/shared/composables/useKeycloak'
import { useStoreApp } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useModulesStore } from '@/stores/modules'
import { useOrganizationStore } from '@/stores/organization'
import { useToolStore } from '@/stores/tool'
import { consumePostLogoutRedirect } from '@/shared/utils/postLogoutRedirect'

import { OperationalModel } from '§M/types/enumerated.type'

// Resource permission mapping for mainapp routes
// Maps URL path segments to permission resource names
const resourceMap: Record<string, string> = {
  projects: 'project',
  comparison: 'comparison',
  gnirut: '', // Experimental features - no permission required
  user: '', // User profile - no permission required
  admin: '' // Admin area - permission checked in page component
}

export const navigationGuard: NavigationGuard = async (to, from, next) => {
  const { isInitialized, isAuthenticated } = useKeycloak()

  // Block navigation if Keycloak isn't initialized yet
  if (!isInitialized.value) {
    return next(false)
  }

  // Always allow access to no-access, no-organization-access, select-organization, and accept-invitation pages
  if (
    to.path === '/no-access'
    || to.path === '/no-organization-access'
    || to.path === '/select-organization'
    || to.path === '/accept-invitation'
  ) {
    return next()
  }

  // If user is not authenticated (e.g. on public routes with check-sso), block navigation to protected routes
  if (!isAuthenticated.value) {
    return next(false)
  }

  // Redirect root to projects page
  if (to.name === '/(with-statusbar)') {
    // After an involuntary logout we stashed the user's location (issue #1776).
    // The router snapshots window.location when createWebHistory() runs at module
    // import — before main.ts can rewrite it — so restoring must happen here, on
    // the router's own initial navigation, rather than via window.history.
    // Re-entering the guard with the restored path applies the org/permission
    // checks below, so a deleted or forbidden target falls back to /projects.
    const restored = consumePostLogoutRedirect()
    if (restored && restored !== to.fullPath) {
      return next(restored)
    }
    return next({ name: '/(with-statusbar)/projects/' })
  }

  // Initialize app store if not initialized (for page reloads)
  const appStore = useStoreApp()
  if (!appStore.isApiConfigInitialized) {
    appStore.initializeApiConfig()
  }

  // Get auth store
  const authStore = useAuthStore()

  // Initialize permissions if not loaded (for page reloads)
  if (!authStore.permissionsLoaded) {
    try {
      const accessControlService = useAccessControlService()
      await accessControlService.initializeCurrentUserPermissions()
    } catch (error) {
      console.error('Failed to initialize permissions in router guard:', error)
      // Allow navigation but permissions will be empty
    }
  }

  // Check if user has access to the mainapp application
  if (!authStore.hasClientAccess) {
    console.warn('User does not have access to mainapp application')
    return next('/no-access')
  }

  // Organization context check
  const organizationStore = useOrganizationStore()
  const toolStore = useToolStore()

  // Try to restore organization from session if not already set
  if (!organizationStore.hasOrganization) {
    organizationStore.initializeFromSession()
  }

  // Refresh permissions and org list on every navigation. Cached perms go stale
  // the moment an admin revokes a role in the current org, and the org list goes
  // stale the moment an admin grants/revokes access to other orgs. Both ride on
  // a single endpoint so this is one request per navigation. Query-only changes
  // stay on the page already checked, and pages like the roadmap write their
  // state to the query on every click, so those skip the request.
  const isQueryOnlyNavigation = from.matched.length > 0 && to.path === from.path
  if (organizationStore.hasOrganization && !isQueryOnlyNavigation) {
    try {
      const accessControlService = useAccessControlService()
      await accessControlService.fetchCurrentUserPermissions(organizationStore.currentOrganizationId)
    } catch (error) {
      console.error('Failed to refresh permissions in router guard:', error)
    }
  }

  // Empty org perms after a successful fetch means the user is locked out of the
  // current org (role revoked, or membership-without-role). Redirect to the
  // dedicated denied page rather than the generic /no-access fallback.
  if (
    organizationStore.hasOrganization
    && authStore.organizationPermissionsLoaded
    && !authStore.hasOrganizationPermissions
  ) {
    organizationStore.clearOrganization()
    toolStore.clearTool()
    authStore.clearPermissions()
    return next('/no-organization-access')
  }

  // If still no organization selected, redirect to organization picker
  if (!organizationStore.hasOrganization) {
    return next({ path: '/select-organization', query: { redirect: to.fullPath } })
  }

  // Tool context check. Projects, locations and concepts are scoped to the selected
  // operational model, so one has to be picked before the projects list means anything.
  const modulesStore = useModulesStore()

  if (!toolStore.hasTool) {
    toolStore.initializeFromSession()
  }

  // Triggers when the organization's subscription to the already-selected tool is
  // revoked mid-session: the selection outlives the access in sessionStorage, and
  // would otherwise filter the projects list down to projects that can no longer be
  // opened. Dropping it here hands the user back to the picker below. Switching
  // organization clears the tool separately, so that path never reaches this.
  if (modulesStore.loaded && toolStore.selectedTool && !modulesStore.hasModuleAccess(toolStore.selectedTool)) {
    toolStore.clearTool()
  }

  // Skipped until modules are known — the same fail-open stance as the module
  // subscription check below, so a failed fetch never locks the user out.
  if (modulesStore.loaded && !toolStore.hasTool) {
    const availableTools = [OperationalModel.AXIOS, OperationalModel.NEXUS].filter((tool) =>
      modulesStore.hasModuleAccess(tool)
    )

    // One subscribed module leaves nothing to choose — select it and skip the page.
    if (availableTools.length === 1) {
      toolStore.setTool(availableTools[0])
    } else if (availableTools.length > 1 && to.path !== '/select-tool') {
      return next({ path: '/select-tool', query: { redirect: to.fullPath } })
    }
  }

  // Extract resource from path (e.g., /(with-statusbar)/projects/123 -> projects)
  const pathSegments = to.path.split('/').filter(Boolean)
  // Skip the layout segment if present
  const resourceSegment = pathSegments.find((seg) => !seg.startsWith('('))

  // The experimental /gnirut tree is a local/dev playground. The routes are
  // excluded from prod builds in vite.config.ts; this is the runtime backstop.
  if (resourceSegment === 'gnirut' && appStore.stateRuntime.mode === 'prod') {
    return next({ name: '/(with-statusbar)/projects/' })
  }

  const resource = resourceSegment ? resourceMap[resourceSegment] : undefined

  // If no resource mapping or it's a public route (empty string), allow access
  if (resource === undefined || resource === '') {
    return next()
  }

  // Check if user has read or list permission for this resource
  if (!authStore.canRead(resource) && !authStore.hasPermission(resource, 'list')) {
    console.warn(`User does not have read or list permission for resource: ${resource}`)
    // Redirect to projects page if no permission (as fallback)
    if (resource !== 'project') {
      return next({ name: '/(with-statusbar)/projects/' })
    }
    return next('/no-access')
  }

  // Module subscription check: deny access to module-specific sub-routes when the
  // organization is not subscribed to the corresponding module. URL-pattern guards
  // catch the cases where the operational_model is encoded in the path (Axios).
  // Nexus and other operational_models that don't show up in the URL are handled
  // page-side via useAssertModuleAccess after the entity is loaded.
  if (modulesStore.loaded) {
    if (to.path.includes('/axios') && !modulesStore.hasModuleAccess('axios')) {
      return next({ path: '/no-module-access', query: { module: 'axios' } })
    }
  }

  next()
}
